import React from "react";
import ReactDOM from "react-dom";
import { HashRouter as Router } from "react-router-dom";
import "components/fontawesome";
import "components/FlaticonIcon";

import App from "./App";
import GlobalContext from "./GlobalContext";
import { getUrlParameter, isTouchDevice } from "utils/utils";
import loadQlikTranslations from "utils/qlikTranslationLoad";
import "utils/classListPolyfill";

// --- Legacy redirects for the NON-ARCHIVE app ---
(function () {
  var loc = window.location;

  // Base from PUBLIC_URL
  var baseRaw = process.env.PUBLIC_URL || "/";
  var base = ("/" + baseRaw).replace(/\/+/g, "/");
  if (!base.endsWith("/")) base += "/";

  // Archive base = <base>/archive/
  var archiveBase = (base + "archive/").replace(/\/+/g, "/");

  // 1) Old "/spi" -> strip "spi"
  var spiPrefix = (base + "spi").replace(/\/+/g, "/");
  if (loc.pathname === spiPrefix || loc.pathname.startsWith(spiPrefix + "/")) {
    var rest = loc.pathname.slice(spiPrefix.length);
    if (rest.startsWith("/")) rest = rest.slice(1);
    var newPath = (base + rest).replace(/\/+/g, "/");
    loc.replace(loc.origin + newPath + (loc.search || "") + (loc.hash || ""));
    return;
  }

  // Already at archive? stop (prevents loops)
  if (loc.pathname.startsWith(archiveBase)) return;

  // 2) Routes that must live under the archive app
  var archiveSlugs = new Set([
    "projects",
    "projectDetails",
    "financing",
    "financingDetails",
    "budget",
    "contractingProcesses",
    "investmentProjects",
    "investmentProjectsLocations",
    "dataAvailability",
    "finPrograms",
    "finProgramsDetails",
    "applications",
  ]);

  // First "#/<slug>" segment
  var m = (loc.hash || "").match(/^#\/([^/?#]+)/);
  var slug = m ? m[1] : null;

  // Redirect slug routes → archive
  if (slug && archiveSlugs.has(slug)) {
    var url1 = loc.origin + archiveBase + (loc.search || "") + (loc.hash || "");
    loc.replace(url1);
    return;
  }

  // 3) NEW: redirect hash "home" (#/) → archive root
  if (loc.hash === "#/") {
    var url2 = loc.origin + archiveBase + (loc.search || "") + "#/";
    loc.replace(url2);
    return;
  }
})();


if (!HTMLCollection.prototype.forEach) HTMLCollection.prototype.forEach = Array.prototype.forEach;

const main = async () => {
  const isSecure = process.env.REACT_APP_QLIK_SERVER_SCHEME === "https"; //isDev ? true : window.location.protocol === "https:";
  const host = process.env.REACT_APP_QLIK_SERVER;
  const port = process.env.REACT_APP_QLIK_SERVER_PORT || 443; //isDev ? 443 : window.location.port;

  const prefix = "/" + process.env.REACT_APP_QLIK_PROXY;

  const config = {
    host,
    prefix,
    port,
    isSecure,
    baseUrl:
      (isSecure ? "https://" : "http://") +
      host +
      (port ? ":" + port : "") +
      prefix +
      "resources",
  };

  const qlikTicket = getUrlParameter("qlikTicket");

  // add require.js to the document
  const requireTag = document.createElement('script');
  requireTag.type = 'text/javascript';
  requireTag.src=`${config.baseUrl}/assets/external/requirejs/require.js` + (qlikTicket ? `?qlikTicket=${qlikTicket}` : '');
  requireTag.id = 'qliksense-require-tag';
  document.head.appendChild(requireTag);
  requireTag.loaded = new Promise((resolve) => {
      requireTag.onload = () => {
          resolve();
      };
  });

  await requireTag.loaded;

  // add qlik style to the document
  const styleTag = document.createElement('link');
  styleTag.type = 'text/css';
  styleTag.rel = 'stylesheet';
  styleTag.href = `${config.baseUrl}/autogenerated/qlik-styles.css`
  styleTag.id = 'qliksense-styles-tag';
  document.head.appendChild(styleTag);
  styleTag.loaded = new Promise((resolve) => {
      styleTag.onload = () => {
          resolve();
      };
  });

  await styleTag.loaded;

  window.require.config(config);

  // console.log(window.require);

  window.require(['js/qlik'], function (qlik) {
    qlik.on("error", function (error) {
      console.error("Qlik error", error);
    });

    const onTouchDevice = isTouchDevice();

    const lang = getUrlParameter("lang");

    // Define possible aliases
    const ukrLangs = ["ua", "uk", "uk-UA"];
    const rusLangs = ["ru", "ru-RU", "rus"];

    // Normalize input
    const normalizedLang = lang ? lang.toLowerCase() : null;

    // Match to Qlik-supported language
    let qlikLang;
    if (ukrLangs.includes(normalizedLang)) {
      qlikLang = "ru"; // Qlik typically uses "ru" for Cyrillic (both UA and RU)
    } else if (rusLangs.includes(normalizedLang)) {
      qlikLang = "ru";
    } else {
      qlikLang = normalizedLang; // fallback to original or browser default
    }

    const langToSet = qlikLang || window.navigator.language;

    //set the language
    // qlik.setLanguage(langToSet);

    qlik.theme.apply('dream-theme');

    window.require(['url-util'], function(urlUtils){
      (async function init() {

        const app = qlik.openApp(process.env.REACT_APP_QLIK_APP, config);

        app.bookmark.apply('42368e1d-9c60-4fbc-b14a-00824ce93a0a');

        function setLanguage(lang) {
          var l = lang ? lang.toUpperCase() : "UK";
          app.field("_Language").selectValues([l], false, false);
        }

        const langCode = langToSet.split("-")[0]; // "uk-UA" → "uk"
        window._languageSelectionPromise = setLanguage(langCode);

        const translations = await loadQlikTranslations(langCode, urlUtils.staticResourcesPath);


        // Window resize handler - for devices like Galaxy Fold, etc
        // and when device is rotated
        (function () {
          window.addEventListener("resize", resizeThrottler, false);
          var resizeTimeout;
          function resizeThrottler() {
            // ignore resize events as long as an actualResizeHandler execution is in the queue
            if (!resizeTimeout) {
              resizeTimeout = setTimeout(function () {
                resizeTimeout = null;
                actualResizeHandler();
                // The actualResizeHandler will execute at a rate of 15fps
              }, 66);
            }
          }

          function actualResizeHandler() {
            qlik.resize();
          }
        })();


        // Router is moved here to be able to navigate history inside App component
        ReactDOM.render(
          <React.StrictMode>
            <GlobalContext.Provider value={{ app, qlik, onTouchDevice, translations }}>
              <Router>
                <App />
              </Router>
            </GlobalContext.Provider>
          </React.StrictMode>,
          document.getElementById("root")
        );
      }());
    });
  });
};

main();
